import { datadogRum } from "@datadog/browser-rum";
import { isEmbedded } from "@commander4/elements/lib/embed-protocol";

/**
 * Initialize Datadog RUM (Real User Monitoring)
 * Monitors frontend performance, errors, and user sessions for JoyIA
 */
export function initDatadog() {
  const applicationId = import.meta.env.VITE_DATADOG_RUM_APPLICATION_ID;
  const clientToken = import.meta.env.VITE_DATADOG_RUM_CLIENT_TOKEN;
  const env = import.meta.env.VITE_DATADOG_ENV || import.meta.env.MODE || "dev";
  const service = import.meta.env.VITE_DATADOG_SERVICE || "joyia-tribes";
  // UST version, supplied by infra alongside env and service. Without it every RUM
  // session reported no version, so a regression could not be tied to a deploy.
  const version = import.meta.env.VITE_DATADOG_VERSION;

  // Only initialize if credentials are available
  if (!applicationId || !clientToken) {
    console.warn("Datadog RUM credentials not found, skipping initialization");
    return;
  }

  try {
    datadogRum.init({
      applicationId,
      clientToken,
      site: "datadoghq.eu",
      service,
      env,
      ...(version ? { version } : {}),
      sessionSampleRate: 100,
      /*
       * Session Replay is suppressed inside the Joyia Web shell, and only there.
       *
       * This app is embedded as an iframe by `packages/web`, and the shell boots several
       * frames at once in the same tab — `me` mounts `hostPrefix: "/"`, so landing on the
       * dashboard already starts two, and opening Help adds a third. Each recorder
       * serialises every DOM mutation it sees, and a user reported the page crawling and
       * then hanging the browser with several running at once.
       *
       * `me`, `news`, `knowledgeflow`, `deploy-web` and `command-center` each took this
       * guard when that was found; these two were missed, which is why this comment is a
       * copy rather than a reference. Only the HOST's recording is ever watched, and it
       * already captures these frames visually, so the extra recorders bought nothing.
       * Error and performance telemetry is untouched; opened standalone in its own tab
       * this app still records normally.
       *
       * `isEmbedded()` is safe at module scope: it reads the `?embedded=1` param the host
       * appends before falling back to a `window.self !== window.top` check, so it is
       * correct for a frame reached by either route.
       */
      sessionReplaySampleRate: isEmbedded() ? 0 : 20,
      defaultPrivacyLevel: "mask-user-input",
    });

    console.log(`Datadog RUM initialized for ${service} (${env})`);
  } catch (error) {
    console.error("Failed to initialize Datadog RUM:", error);
  }
}
